import { Form as DxForm, FormRef as DXFormRef, GroupItem, SimpleItem, ValidationRule } from 'devextreme-react/form'
import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import { ChildFormHandle } from '@/routes/admin/users/user/forms/types'
import { useMutation, useQuery } from '@tanstack/react-query'
import UserStyles from '@/routes/admin/users/user/User.module.scss'
import { HashLoader } from 'react-spinners'
import RouterErrorHandler from '@/components/RouterErrorHandler'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { user } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { ArraySourceComposer } from '@/auth/api/connector'

export type BasicUserDataProps = {
  userId: number
}

const QS_INFORMATICA_PIVA = '01955220346'

export const BasicUserData = forwardRef<ChildFormHandle, BasicUserDataProps>((props, ref) => {
  const qsadminapiService = useQsAdminApiManager()
  const formRef = useRef<HTMLFormElement>(null)
  const dxFormRef = useRef<DXFormRef>(null)

  const validateCurrentForm = () => {
    return dxFormRef.current?.instance().validate().isValid === true
  }

  useImperativeHandle(ref, () => ({
    validate: validateCurrentForm,
    submit: () => {
      formRef.current?.requestSubmit()
    },
  }))

  const fetcher = useQuery({
    queryKey: ['basicUserData', props.userId],
    queryFn: () => {
      return qsadminapiService.service.user(props.userId).query((builder) => {
        builder.expand('reparto')
        builder.expand('azienda')
        builder.expand('manager')
      })
    },
    refetchOnWindowFocus: false,
  })
  const repartoFetcher = useQuery({
    queryKey: ['reparto'],
    queryFn: () => {
      return qsadminapiService.service.reparto().query()
    },
    refetchOnWindowFocus: false,
  })
  const aziendaFetcher = useQuery({
    queryKey: ['azienda'],
    queryFn: () => {
      return qsadminapiService.service.azienda().query((builder) => {
        builder.select('id', 'nome')
      })
    },
    refetchOnWindowFocus: false,
  })
  const managerFetcher = useQuery({
    queryKey: ['userManager', props.userId],
    queryFn: () => {
      return qsadminapiService.service.user().query((builder, currentUser) => {
        builder.select('id', 'fullname')
        builder.filter(
          currentUser.id
            .ne(props.userId)
            .and(currentUser.azienda.props.p_iva.eq(QS_INFORMATICA_PIVA))
            .and(currentUser.attivo.eq(true)),
        )
        builder.orderBy(currentUser.fullname.asc())
      })
    },
    enabled: fetcher.data?.data?.azienda?.p_iva === QS_INFORMATICA_PIVA,
    refetchOnWindowFocus: false,
  })

  const patcher = useMutation({
    mutationFn: (formData: user) => {
      return qsadminapiService.service.user(props.userId).patch(formData)
    },
  })

  if (fetcher.isLoading)
    return (
      <div className={UserStyles.loaderWrapper}>
        <HashLoader loading={fetcher.isLoading} size={100} color={'#ff5722'} />
      </div>
    )
  if (fetcher.error) return <RouterErrorHandler />

  const isQsInformaticaUser = fetcher.data?.data?.azienda?.p_iva === QS_INFORMATICA_PIVA

  return (
    <>
      <form
        ref={formRef}
        onSubmit={(e) => {
          e.preventDefault()
          if (!validateCurrentForm()) return
          if (fetcher.data?.data) patcher.mutate(fetcher.data?.data)
        }}
        style={{ marginTop: '10px' }}
      >
        <DxForm ref={dxFormRef} formData={fetcher.data?.data} showValidationSummary={true}>
          <GroupItem caption="Dati Utente" colCount={4}>
            <GroupItem colSpan={4} colCount={5}>
              <SimpleItem
                dataField={'id'}
                label={{
                  text: 'ID',
                }}
                colSpan={1}
                editorOptions={{
                  disabled: true,
                }}
              />
              <SimpleItem
                dataField={'cognome'}
                colSpan={2}
                label={{
                  text: 'Cognome',
                }}
              />
              <SimpleItem
                dataField={'nome'}
                colSpan={2}
                label={{
                  text: 'Nome',
                }}
              />
            </GroupItem>
            <GroupItem colSpan={4} colCount={2}>
              <SimpleItem
                dataField={'email'}
                label={{
                  text: 'Email',
                }}
              />
              <SimpleItem
                dataField={'email_ad'}
                label={{
                  text: 'Email AD',
                }}
              />
            </GroupItem>
            <GroupItem colSpan={4} colCount={isQsInformaticaUser ? 3 : 2}>
              {repartoFetcher.data?.data && (
                <SimpleItem
                  dataField="reparto"
                  editorType="dxLookup"
                  editorOptions={{
                    dataSource: ArraySourceComposer('id', repartoFetcher.data?.data?.value),
                    placeholder: 'Seleziona il reparto...',
                    displayExpr: 'nome',
                    searchEnabled: true,
                    dropDownCentered: true,
                    dropDownOptions: {
                      showTitle: true,
                      title: 'Selezionare il reparto',
                      hideOnOutsideClick: true,
                    },
                  }}
                >
                  <ValidationRule type="required" message="Reparto richiesto" />
                </SimpleItem>
              )}
              {aziendaFetcher.data?.data && (
                <SimpleItem
                  dataField="azienda"
                  editorType="dxLookup"
                  editorOptions={{
                    dataSource: ArraySourceComposer('id', aziendaFetcher.data?.data?.value),
                    placeholder: 'Seleziona azienda...',
                    displayExpr: 'nome',
                    searchEnabled: true,
                    dropDownCentered: true,
                    dropDownOptions: {
                      showTitle: true,
                      title: 'Selezionare azienda',
                      hideOnOutsideClick: true,
                    },
                  }}
                ></SimpleItem>
              )}
              {isQsInformaticaUser && managerFetcher.data?.data && (
                <SimpleItem
                  dataField="manager"
                  editorType="dxLookup"
                  editorOptions={{
                    dataSource: ArraySourceComposer('id', managerFetcher.data?.data?.value),
                    placeholder: 'Seleziona manager...',
                    displayExpr: 'fullname',
                    searchEnabled: true,
                    showClearButton: true,
                    readOnly: true,
                    dropDownCentered: true,
                    dropDownOptions: {
                      showTitle: true,
                      title: 'Selezionare manager',
                      hideOnOutsideClick: true,
                    },
                  }}
                ></SimpleItem>
              )}
              <SimpleItem
                colSpan={isQsInformaticaUser ? 3 : 2}
                dataField={'note'}
                label={{
                  text: 'Note',
                }}
                editorType={'dxTextArea'}
                editorOptions={{
                  height: 100,
                }}
              />
            </GroupItem>
            <GroupItem colSpan={4} caption="Impostazioni utente" colCount={2}>
              <SimpleItem
                dataField={'attivo'}
                label={{
                  text: 'Attivo',
                  location: 'top',
                  alignment: 'left',
                }}
                editorType={'dxSwitch'}
              />
              <SimpleItem
                dataField={'contractor'}
                label={{
                  text: 'Contractor',
                  location: 'top',
                  alignment: 'left',
                }}
                editorType={'dxSwitch'}
              />
            </GroupItem>
          </GroupItem>
        </DxForm>
      </form>
    </>
  )
})

BasicUserData.displayName = 'BasicUserData'
